import React, { useState, useEffect, useCallback } from "react";

const MouseFollowingArrow = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [arrowPosition, setArrowPosition] = useState({ x: 0, y: 0 });
  const [additionalRotation, setAdditionalRotation] = useState(0);
  const [isMouseMoving, setIsMouseMoving] = useState(false);
  const [arrowSize, setArrowSize] = useState(1);
  const [isArrowVisible, setIsArrowVisible] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [aPressCount, setAPressCount] = useState(0);
  const [lastAPressTime, setLastAPressTime] = useState(0);

  const loggedSetAPressCount = useCallback(
    (value) => {
      const evaluatedValue =
        typeof value === "function" ? value(aPressCount) : value;
      setAPressCount(evaluatedValue);
    },
    [aPressCount]
  );

  const handleAPress = useCallback(() => {
    if (isArrowVisible) {
      setIsArrowVisible(false);
      loggedSetAPressCount(0);
      return;
    }

    const currentTime = Date.now();
    if (currentTime - lastAPressTime <= 1000) {
      loggedSetAPressCount((prevCount) => prevCount + 1);
    } else {
      loggedSetAPressCount(1);
    }
    setLastAPressTime(currentTime);

    if (aPressCount + 1 === 5) {
      setIsArrowVisible(true);
      loggedSetAPressCount(0);
    }
  }, [aPressCount, lastAPressTime, isArrowVisible, loggedSetAPressCount]);

  useEffect(() => {
    let moveTimer;

    const handleMouseMove = (event) => {
      const newPosition = { x: event.clientX, y: event.clientY };
      setMousePosition(newPosition);
      setArrowPosition(newPosition);
      setIsMouseMoving(true);
      setAdditionalRotation(0);

      clearTimeout(moveTimer);
      moveTimer = setTimeout(() => {
        setIsMouseMoving(false);
      }, 100);
    };

    const handleKeyPress = (event) => {
      if (isInputFocused) return;

      if (event.key === "a" || event.key === "A") {
        handleAPress();
        return;
      }

      if (!isMouseMoving) {
        const moveStep = 10;
        switch (event.key) {
          case "r":
          case "R":
            const rotationAmount = event.shiftKey ? -10 : 10;
            setAdditionalRotation(
              (prev) => (prev + rotationAmount + 360) % 360
            );
            break;
          case "s":
          case "S":
            setArrowSize((prev) =>
              event.shiftKey
                ? Math.max(0.5, prev - 0.1)
                : Math.min(2, prev + 0.1)
            );
            break;
          case "ArrowUp":
            if (event.shiftKey) {
              setArrowPosition((prev) => ({
                ...prev,
                y: Math.max(0, prev.y - moveStep),
              }));
            }
            break;
          case "ArrowDown":
            if (event.shiftKey) {
              setArrowPosition((prev) => ({
                ...prev,
                y: Math.min(window.innerHeight, prev.y + moveStep),
              }));
            }
            break;
          case "ArrowLeft":
            if (event.shiftKey) {
              setArrowPosition((prev) => ({
                ...prev,
                x: Math.max(0, prev.x - moveStep),
              }));
            }
            break;
          case "ArrowRight":
            if (event.shiftKey) {
              setArrowPosition((prev) => ({
                ...prev,
                x: Math.min(window.innerWidth, prev.x + moveStep),
              }));
            }
            break;
        }
      }
    };

    const handleFocus = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      ) {
        setIsInputFocused(true);
      }
    };

    const handleBlur = () => {
      setIsInputFocused(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("keydown", handleKeyPress);
    document.addEventListener("focus", handleFocus, true);
    document.addEventListener("blur", handleBlur, true);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("keydown", handleKeyPress);
      document.removeEventListener("focus", handleFocus, true);
      document.removeEventListener("blur", handleBlur, true);
      clearTimeout(moveTimer);
    };
  }, [isMouseMoving, isInputFocused, handleAPress]);

  const calculateArrowAngle = () => {
    const windowCenterX = window.innerWidth / 2;
    const windowCenterY = window.innerHeight / 2;
    const dx = mousePosition.x - windowCenterX;
    const dy = mousePosition.y - windowCenterY;
    return Math.atan2(dy, dx) * (180 / Math.PI);
  };

  const baseAngle = calculateArrowAngle();
  const finalAngle = baseAngle + additionalRotation;
  const baseArrowLength = 30;
  const arrowLength = baseArrowLength * arrowSize;

  const offsetX = Math.cos((finalAngle * Math.PI) / 180) * arrowLength;
  const offsetY = Math.sin((finalAngle * Math.PI) / 180) * arrowLength;

  const arrowStyle = {
    content: '""',
    position: "fixed",
    top: `${arrowPosition.y - offsetY}px`,
    left: `${arrowPosition.x - offsetX}px`,
    transform: `translate(-50%, -50%) rotate(${finalAngle}deg) scale(${arrowSize})`,
    width: "60px",
    height: "60px",
    backgroundColor: "rgba(255, 0, 0, 0.6)",
    clipPath:
      "polygon(0% 25%, 50% 25%, 50% 0%, 100% 50%, 50% 100%, 50% 75%, 0% 75%)",
    opacity: isArrowVisible ? 1 : 0,
    transition: "opacity 0.3s ease",
    pointerEvents: "none",
    zIndex: 9999,
  };

  return <div style={arrowStyle} data-testid="mouse-following-arrow" />;
};

export default MouseFollowingArrow;
